<template>
  <DropdownHybrid
    ref="dropdown" v-model="modelValue"
    :sheet-title="t('page.bookmark.tags.sheet')"
    :menu-props="menuProps"
  >
    <template #activator>
      <div ref="currentElement" />
    </template>

    <v-card-text class="px-4 pb-0 pt-0 pt-md-3">
      <v-chip-group v-model="selection" color="blue400" base-color="neutral30" column multiple>
        <v-chip
          v-for="tag in bookmark.tags" :key="tag.id"
           class="rounded-lg"
          :value="tag.id"
          :variant="selection.includes(tag.id) ? 'tonal' : 'flat'"
        >
          {{ tag.name }}
        </v-chip>
      </v-chip-group>
    </v-card-text>

    <v-card-actions class="px-4 pb-4">
      <v-row dense>
        <v-col cols="12">
          <BrandButton
            variant="primary" size="large" block type="submit" :disabled="!hasChanges"
            @click="onApply"
          >
            {{ t('cta.update_tags_filter') }}
          </BrandButton>
        </v-col>
        <v-col v-if="canManageTags" cols="12">
          <BrandButton
            variant="basic" block
            @click="$emit('edit')"
          >
            {{ t('cta.manage_tags') }}
          </BrandButton>
        </v-col>
      </v-row>
    </v-card-actions>
  </DropdownHybrid>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { sleep } from '@/utils/helpers'
import CollectionMember from '@/store/models/collections/CollectionMember'
import { buildPermissionContext, canPerformItemAction, CollectionItemAction } from '@/utils/permissions/collection-item'

const props = defineProps({
  modelValue: Boolean,
  activator: {
    type: [String, Object],
    default: undefined,
  },
  // Accepts both Bookmark and CollectionItem (when resourceType is 'bookmark')
  bookmark: {
    type: Object,
    required: true,
  },
  userId: {
    type: String,
    default: null,
  },
  membership: {
    type: Object,
    default: null,
  },
  publicMode: {
    type: Boolean,
    default: false,
  },
})

// Get membership from store if bookmark is in a shared collection
const resolvedMembership = computed(() => {
  // If membership is passed as a prop (e.g. from public collection), use it
  if (props.membership) return props.membership
  if (!props.bookmark) return null
  // Private collection - always has full permissions
  if (props.bookmark.isPrivate) return { isOwner: true }
  // Get membership from store for shared collections
  return CollectionMember.query()
    .where('collectionId', props.bookmark.ownerId)
    .where('userId', props.userId)
    .first()
})

// Check if user can manage tags
const canManageTags = computed(() => {
  if (props.publicMode) return false
  if (!props.bookmark) return false
  const context = buildPermissionContext({
    membership: resolvedMembership.value,
    item: props.bookmark,
    userId: props.userId,
  })
  return canPerformItemAction(context, CollectionItemAction.MANAGE_TAGS)
})

const emit = defineEmits(['edit', 'submit', 'update:modelValue'])
const { t } = useI18n()
const store = useStore()
const selection = ref([])
const dropdown = ref(null)
const currentElement = ref(null)

// Use collectionItems store for filters (unified abstraction)
const filters = computed(() => store.getters['collectionItems/filters'])
const currentFilters = computed(() => filters.value.tags)

const modelValue = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val),
})


const menuProps = computed(() => {
  return {
    maxWidth: 340,
    activator: currentElement.value,
    openOnClick: false,
    closeOnContentClick: false,
    offset: [-30, 0],
    contentClass: 'filter-tags-dropdown',
  }
})

const hasChanges = computed(() => {
  return currentFilters.value.join('-') !== selection.value.join('-')
})

// Watch for changes in modelValue
watch(() => modelValue.value, () => {
  selection.value = [...currentFilters.value]
})

// Watch for changes in currentFilters
watch(currentFilters, () => {
  selection.value = [...currentFilters.value]
}, { immediate: true })

async function onApply() {
  const tags = [...selection.value]
  dropdown.value?.close()

  // FIXME DEBUG replace this with a debounce in All.vue watcher
  // wait for dropdown close transition properly
  await sleep(200)

  emit('submit', tags)
}
</script>

<style lang="scss" scoped>
.filter-tags-dropdown {
  max-height: 600px !important;
}

.v-chip:not(.v-chip--active):focus::before {
  opacity: 0;
}

// Fix disabled BrandButton styling to match other components
:deep(.v-btn.brand-btn.brand-btn--primary.v-btn--disabled) {
  background: rgba(var(--v-theme-on-surface), 0.12) !important;
  color: rgba(var(--v-theme-on-surface), 0.38) !important;
}
</style>
